// Cmd/Ctrl + K palette wrapping cmdk + the Dialog primitive.
// Apps wire it to /api/search/suggest in Phase 6.

import { Command as CmdkPrimitive } from "cmdk";
import { Search } from "lucide-react";
import { forwardRef } from "react";
import { cn } from "../cn.ts";
import { Dialog, DialogContent } from "./dialog.tsx";

export interface SearchCommandProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  placeholder?: string;
  value: string;
  onValueChange: (next: string) => void;
  children: React.ReactNode;
}

export function SearchCommand({
  open,
  onOpenChange,
  placeholder,
  value,
  onValueChange,
  children,
}: SearchCommandProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl gap-0 p-0" hideClose>
        <CmdkPrimitive className="overflow-hidden rounded-2xl bg-[var(--brand-paper)]">
          <div className="flex items-center gap-2 border-b border-[var(--brand-border)] px-4">
            <Search className="h-4 w-4 text-[var(--brand-muted)]" aria-hidden />
            <CmdkPrimitive.Input
              value={value}
              onValueChange={onValueChange}
              placeholder={placeholder ?? "Search Bloom…"}
              className="flex h-12 w-full bg-transparent text-sm outline-none placeholder:text-[var(--brand-muted)]"
            />
          </div>
          <CmdkPrimitive.List className="max-h-[60vh] overflow-y-auto p-2">
            {children}
          </CmdkPrimitive.List>
        </CmdkPrimitive>
      </DialogContent>
    </Dialog>
  );
}

export const Command = CmdkPrimitive;

export const CommandItem = forwardRef<
  React.ElementRef<typeof CmdkPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CmdkPrimitive.Item>
>(function CommandItem({ className, ...rest }, ref) {
  return (
    <CmdkPrimitive.Item
      ref={ref}
      className={cn(
        "flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm text-[var(--brand-ink)] aria-selected:bg-[var(--brand-pink-soft)] data-[selected=true]:bg-[var(--brand-pink-soft)]",
        className,
      )}
      {...rest}
    />
  );
});

export const CommandGroup = forwardRef<
  React.ElementRef<typeof CmdkPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof CmdkPrimitive.Group>
>(function CommandGroup({ className, ...rest }, ref) {
  return (
    <CmdkPrimitive.Group
      ref={ref}
      className={cn(
        "overflow-hidden p-1 text-[var(--brand-ink)] [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-[var(--brand-muted)]",
        className,
      )}
      {...rest}
    />
  );
});

export const CommandEmpty = forwardRef<
  React.ElementRef<typeof CmdkPrimitive.Empty>,
  React.ComponentPropsWithoutRef<typeof CmdkPrimitive.Empty>
>(function CommandEmpty({ className, ...rest }, ref) {
  return (
    <CmdkPrimitive.Empty
      ref={ref}
      className={cn("py-6 text-center text-sm text-[var(--brand-muted)]", className)}
      {...rest}
    />
  );
});
